import React, { createContext, useCallback, useContext, useState } from "react";
import { CargoReportResponse, getReportListaCargo } from "../../services/cargoService";
import { getErrorMessage } from "../../locales/translationUtils";
import LoadingOverlay from "../../components/Loading";
import { ApiResponse } from "../../services/api";

interface CargoContextProps {
  cargos: CargoReportResponse[] | null;
  loading: boolean;
  error: string | null;
  fetchCargos: (filters: any, released?: boolean) => void;
  apiResponse: ApiResponse<CargoReportResponse> | null;
}

interface CargoProviderProps {
  children: React.ReactNode;
}

const CargoContext = createContext<CargoContextProps | null>(null);

const CargoReportProvider: React.FC<CargoProviderProps> = ({ children }) => {
  const [cargos, setCargos] = useState<CargoReportResponse[] | null>(null);
  const [apiResponse, setApiResponse] = useState<ApiResponse<CargoReportResponse> | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCargos = useCallback(async (filters: any) => {
    setError(null);

    try {
      setLoading(true);
      filters.paginated = true;
      const response = await getReportListaCargo(filters);
      const items: CargoReportResponse[] = response.data.values;

      setApiResponse(response);
      setCargos(items || []);
    } catch (err) {
      setCargos([]);
      setError(getErrorMessage("fetchCargosError"));
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <CargoContext.Provider value={{ cargos, apiResponse, loading, error, fetchCargos }}>
      <LoadingOverlay isLoading={loading} />
      {children}
    </CargoContext.Provider>
  );
};

export default CargoReportProvider;

export const useCargo = () => {
  const context = useContext(CargoContext);
  if (!context) {
    throw new Error(
      getErrorMessage("wrongUseProviderError", {
        auth: useCargo?.name,
        providerName: CargoReportProvider?.name,
      })
    );
  }
  return context;
};
